import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  FileText,
  Loader2,
  Package,
  Plus,
  Send,
  Shield,
  ShieldOff,
  Ticket,
  Trash2,
} from 'lucide-react';
import {
  changerStatutAssuranceSinistre,
  creerAssuranceBien,
  creerAssuranceContrat,
  declarerAssuranceSinistre,
  fetchAssurance,
  supprimerAssuranceBien,
  supprimerAssuranceContrat,
  transmettreAssuranceSinistre,
  type AssuranceContrat,
  type AssuranceSinistre,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';

/**
 * Les assurances : contrats, échéances, attestations, sinistres, inventaire.
 *
 * LE TABLEAU DE BORD DIT D'ABORD CE QUI MANQUE
 * ---------------------------------------------------------------------------
 * Pas seulement ce qu'on a : les biens SANS couverture, les bénévoles SANS
 * individuelle accident — la RC ne couvre pas leurs dommages corporels, et
 * c'est l'angle mort classique. Un module d'assurance qui ne montre que les
 * contrats en règle rassure à tort.
 *
 * L'onglet vit dans l'URL — le menu latéral porte les quatre entrées, et
 * chacune ouvre SON écran, se partage en lien, survit au rechargement.
 */

const euros = (centimes: number | null) =>
  centimes !== null ? (centimes / 100).toLocaleString('fr-FR', { minimumFractionDigits: 2 }) + ' €' : '—';

const dateFr = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('fr-FR') : '—';

export default function AssurancePage() {
  const { isLoading: authLoading, aLeDroit } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const peutVoir = aLeDroit('voir_assurance');
  const peutGerer = aLeDroit('gerer_assurance');

  const { onglet: ongletUrl } = useParams();
  const ONGLETS = ['contrats', 'attestations', 'sinistres', 'biens'] as const;
  type Onglet = (typeof ONGLETS)[number];
  const onglet: Onglet = (ONGLETS as readonly string[]).includes(ongletUrl ?? '')
    ? (ongletUrl as Onglet)
    : 'contrats';
  const setOnglet = (o: Onglet) => navigate(`/bureau/assurance/${o}`);

  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);

  const { data, isPending } = useQuery({ queryKey: ['assurance'], queryFn: fetchAssurance, enabled: peutVoir });

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['assurance'] });
  const surErreur = (e: Error) => setNotice({ type: 'erreur', texte: e.message });

  if (authLoading || (peutVoir && isPending)) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!peutVoir) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              Les assurances dépendent de votre fonction au bureau : un administrateur peut les
              ouvrir dans la grille de droits.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const bord = data?.tableauDeBord;

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <Shield className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">Assurances</h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Contrats, échéances, attestations pour les mairies, sinistres.
          </p>
        </div>
      </div>

      {notice && (
        <div
          className={`card text-sm flex items-start gap-3 ${
            notice.type === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1">{notice.texte}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {/* Le tableau de bord : d'abord ce qui manque. */}
      {bord && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <CarteChiffre libelle="Contrats actifs" valeur={bord.contratsActifs} alerte={bord.contratsActifs === 0} />
          <CarteChiffre libelle="Échéances sous 60 j" valeur={bord.echeances60Jours} alerte={bord.echeances60Jours > 0} />
          <CarteChiffre libelle="Sinistres ouverts" valeur={bord.sinistresOuverts} alerte={bord.sinistresOuverts > 0} />
          <CarteChiffre libelle="Biens non couverts" valeur={bord.biensNonCouverts} alerte={bord.biensNonCouverts > 0} />
        </div>
      )}

      {bord && !bord.benevolesCouverts && (
        <div className="card bg-amber-50 border-amber-200 text-amber-800 flex items-start gap-3 text-sm">
          <ShieldOff className="w-5 h-5 mt-0.5 shrink-0" />
          <p>
            <strong>Aucune « individuelle accident » active.</strong> La responsabilité civile ne
            couvre pas les dommages corporels que subissent les bénévoles eux-mêmes : un bénévole
            blessé en montant la buvette n'est indemnisé par personne. C'est l'angle mort classique
            des associations — parlez-en à votre assureur.
          </p>
        </div>
      )}

      <nav aria-label="Sections des assurances" className="flex gap-2 flex-wrap">
        {(
          [
            ['contrats', 'Contrats', FileText],
            ['attestations', 'Attestations', Ticket],
            ['sinistres', 'Sinistres', AlertTriangle],
            ['biens', 'Biens assurés', Package],
          ] as const
        ).map(([code, libelle, Icone]) => (
          <button
            key={code}
            type="button"
            onClick={() => setOnglet(code)}
            aria-current={onglet === code ? 'page' : undefined}
            className={`flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm transition-colors ${
              onglet === code
                ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-800 dark:text-primary-200'
                : 'border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-primary-300'
            }`}
          >
            <Icone className="w-4 h-4" />
            {libelle}
          </button>
        ))}
      </nav>

      {onglet === 'contrats' && (
        <Contrats
          contrats={data?.contrats ?? []}
          documents={data?.documentsAssurance ?? []}
          peutGerer={peutGerer}
          onFait={rafraichir}
          onErreur={surErreur}
        />
      )}
      {onglet === 'attestations' && (
        <Attestations contrats={data?.contrats ?? []} evenements={data?.evenements ?? []} />
      )}
      {onglet === 'sinistres' && (
        <Sinistres
          sinistres={data?.sinistres ?? []}
          contrats={data?.contrats ?? []}
          evenements={data?.evenements ?? []}
          peutGerer={peutGerer}
          onFait={rafraichir}
          onErreur={surErreur}
        />
      )}
      {onglet === 'biens' && (
        <Biens
          biens={data?.biens ?? []}
          contrats={data?.contrats ?? []}
          peutGerer={peutGerer}
          onFait={rafraichir}
          onErreur={surErreur}
        />
      )}
    </div>
  );
}

function CarteChiffre({ libelle, valeur, alerte }: { libelle: string; valeur: number; alerte: boolean }) {
  return (
    <div className="card">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{libelle}</p>
      <p className={`text-2xl font-bold tabular-nums mt-1 ${
        alerte ? 'text-amber-600 dark:text-amber-400' : 'text-gray-900 dark:text-gray-100'
      }`}>
        {valeur}
      </p>
    </div>
  );
}

/* --------------------------------- Contrats -------------------------------- */

function Contrats({
  contrats,
  documents,
  peutGerer,
  onFait,
  onErreur,
}: {
  contrats: AssuranceContrat[];
  documents: { id: number; titre: string }[];
  peutGerer: boolean;
  onFait: () => void;
  onErreur: (e: Error) => void;
}) {
  const [type, setType] = useState('rc');
  const [assureur, setAssureur] = useState('');
  const [numero, setNumero] = useState('');
  const [debut, setDebut] = useState('');
  const [fin, setFin] = useState('');
  const [prime, setPrime] = useState('');
  const [document, setDocument] = useState<number | ''>('');

  const creer = useMutation({
    mutationFn: () =>
      creerAssuranceContrat({
        type,
        assureur,
        numero,
        debut,
        fin,
        primeCentimes: prime === '' ? null : Math.round(Number(prime) * 100),
        document: document === '' ? null : document,
      }),
    onSuccess: () => {
      setAssureur('');
      setNumero('');
      setPrime('');
      onFait();
    },
    onError: onErreur,
  });

  const retirer = useMutation({
    mutationFn: (id: number) => supprimerAssuranceContrat(id),
    onSuccess: onFait,
    onError: onErreur,
  });

  return (
    <div className="space-y-4">
      {peutGerer && (
        <form
          className="card grid gap-3 sm:grid-cols-3 lg:grid-cols-4"
          onSubmit={(e) => {
            e.preventDefault();
            creer.mutate();
          }}
        >
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Type
            <select value={type} onChange={(e) => setType(e.target.value)} className="input w-full mt-0.5">
              <option value="rc">Responsabilité civile</option>
              <option value="multirisques">Multirisques association</option>
              <option value="evenementielle">Événement / spectacle</option>
              <option value="individuelle_accident">Individuelle accident bénévole</option>
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Assureur
            <input required value={assureur} onChange={(e) => setAssureur(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            N° de contrat
            <input required value={numero} onChange={(e) => setNumero(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Prime annuelle (€)
            <input type="number" step="0.01" min="0" value={prime} onChange={(e) => setPrime(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Début
            <input required type="date" value={debut} onChange={(e) => setDebut(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Fin
            <input required type="date" value={fin} onChange={(e) => setFin(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400 sm:col-span-2">
            Pièce (bibliothèque, catégorie Assurance)
            <select value={document} onChange={(e) => setDocument(e.target.value === '' ? '' : Number(e.target.value))} className="input w-full mt-0.5">
              <option value="">— aucune —</option>
              {documents.map((d) => (
                <option key={d.id} value={d.id}>{d.titre}</option>
              ))}
            </select>
          </label>
          <button type="submit" disabled={creer.isPending} className="btn-primary flex items-center gap-2 w-fit">
            <Plus className="w-4 h-4" /> Enregistrer le contrat
          </button>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-1.5 pr-3">Type</th>
              <th className="py-1.5 pr-3">Assureur</th>
              <th className="py-1.5 pr-3">N°</th>
              <th className="py-1.5 pr-3">Période</th>
              <th className="py-1.5 pr-3">Prime</th>
              <th className="py-1.5 pr-3">État</th>
              {peutGerer && <th className="py-1.5">Retirer</th>}
            </tr>
          </thead>
          <tbody>
            {contrats.map((c) => (
              <tr key={c.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-2 pr-3">{c.typeLibelle}</td>
                <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">{c.assureur}</td>
                <td className="py-2 pr-3 font-mono text-xs">{c.numero}</td>
                <td className="py-2 pr-3 whitespace-nowrap">{dateFr(c.debut)} → {dateFr(c.fin)}</td>
                <td className="py-2 pr-3 tabular-nums">{euros(c.primeCentimes)}</td>
                <td className="py-2 pr-3">
                  <EtatContrat contrat={c} />
                </td>
                {peutGerer && (
                  <td className="py-2">
                    <button
                      type="button"
                      onClick={() => retirer.mutate(c.id)}
                      aria-label={`Retirer le contrat ${c.numero}`}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {contrats.length === 0 && (
              <tr><td colSpan={7} className="py-4 text-center text-gray-500">Aucun contrat enregistré.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EtatContrat({ contrat }: { contrat: AssuranceContrat }) {
  if (contrat.resilieLe) {
    return <span className="rounded-full px-2 py-0.5 text-xs bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">Résilié</span>;
  }
  if (!contrat.actif) {
    return <span className="rounded-full px-2 py-0.5 text-xs bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200">Expiré</span>;
  }
  if (contrat.joursRestants !== null && contrat.joursRestants <= 60) {
    return (
      <span className="rounded-full px-2 py-0.5 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
        ⚠ {contrat.joursRestants} j
      </span>
    );
  }
  return <span className="rounded-full px-2 py-0.5 text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">Actif</span>;
}

/* ------------------------------- Attestations ------------------------------ */

function Attestations({
  contrats,
  evenements,
}: {
  contrats: AssuranceContrat[];
  evenements: { id: number; nom: string; date: string | null; lieu: string | null }[];
}) {
  const [contrat, setContrat] = useState<number | ''>('');

  // Seuls les contrats capables de couvrir une organisation, et actifs.
  const eligibles = contrats.filter((c) => c.actif && (c.type === 'rc' || c.type === 'evenementielle'));

  return (
    <div className="space-y-4">
      <p className="card text-sm text-gray-600 dark:text-gray-400">
        Le récapitulatif RC organisateur est la pièce que la mairie demande pour prêter la salle ou
        l'espace public. Il est régénéré à chaque édition et refuse de mentir : contrat expiré ou
        expirant avant l'événement, pas d'attestation. <strong>Seule l'attestation émise par votre
        assureur fait foi</strong> — déposez-la dans la bibliothèque, catégorie Assurance.
      </p>

      <div className="card space-y-3">
        <label className="block text-sm text-gray-600 dark:text-gray-400 max-w-md">
          Contrat couvrant l'organisation
          <select value={contrat} onChange={(e) => setContrat(e.target.value === '' ? '' : Number(e.target.value))} className="input w-full mt-0.5">
            <option value="">Choisir…</option>
            {eligibles.map((c) => (
              <option key={c.id} value={c.id}>{c.assureur} — {c.numero} ({c.typeLibelle})</option>
            ))}
          </select>
        </label>
        {eligibles.length === 0 && (
          <p className="text-sm text-amber-700 dark:text-amber-400">
            Aucun contrat actif de type RC ou événementiel : enregistrez-le d'abord dans l'onglet Contrats.
          </p>
        )}

        <ul className="divide-y divide-gray-100 dark:divide-gray-700">
          {evenements.map((e) => (
            <li key={e.id} className="py-2 flex items-center gap-3 flex-wrap">
              <span className="font-medium text-gray-900 dark:text-gray-100">{e.nom}</span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{dateFr(e.date)} · {e.lieu ?? 'lieu à préciser'}</span>
              {/* Une vraie adresse : le PDF s'ouvre ou se télécharge, y compris au doigt. */}
              <a
                className={`ml-auto text-sm underline ${contrat === '' ? 'pointer-events-none text-gray-400' : 'text-primary-700 dark:text-primary-300'}`}
                href={contrat === '' ? '#' : `/api/assurance/evenements/${e.id}/attestation?contrat=${contrat}`}
                aria-disabled={contrat === ''}
              >
                Attestation PDF
              </a>
            </li>
          ))}
          {evenements.length === 0 && (
            <li className="py-3 text-sm text-gray-500">Aucun événement à venir : créez-le d'abord dans Événements.</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/* -------------------------------- Sinistres -------------------------------- */

function Sinistres({
  sinistres,
  contrats,
  evenements,
  peutGerer,
  onFait,
  onErreur,
}: {
  sinistres: AssuranceSinistre[];
  contrats: AssuranceContrat[];
  evenements: { id: number; nom: string }[];
  peutGerer: boolean;
  onFait: () => void;
  onErreur: (e: Error) => void;
}) {
  const [contrat, setContrat] = useState<number | ''>('');
  const [type, setType] = useState('dommage_materiel');
  const [survenuLe, setSurvenuLe] = useState('');
  const [lieu, setLieu] = useState('');
  const [description, setDescription] = useState('');
  const [montant, setMontant] = useState('');
  const [evenement, setEvenement] = useState<number | ''>('');

  const declarer = useMutation({
    mutationFn: () =>
      declarerAssuranceSinistre({
        contrat,
        type,
        survenuLe,
        lieu,
        description,
        montantEstimeCentimes: montant === '' ? null : Math.round(Number(montant) * 100),
        evenement: evenement === '' ? null : evenement,
      }),
    onSuccess: () => {
      setLieu('');
      setDescription('');
      setMontant('');
      onFait();
    },
    onError: onErreur,
  });

  const transmettre = useMutation({
    mutationFn: (id: number) => transmettreAssuranceSinistre(id),
    onSuccess: onFait,
    onError: onErreur,
  });

  const changerStatut = useMutation({
    mutationFn: ({ id, statut }: { id: number; statut: string }) => changerStatutAssuranceSinistre(id, statut),
    onSuccess: onFait,
    onError: onErreur,
  });

  return (
    <div className="space-y-4">
      <p className="card bg-amber-50 border-amber-200 text-amber-800 text-sm">
        Les contrats portent des délais de déclaration — cinq jours ouvrés le plus souvent,
        <strong> deux pour un vol</strong>. Déclarer ici ne suffit pas : c'est « Transmettre à
        l'assureur » qui fige la date que ces délais regardent.
      </p>

      {peutGerer && (
        <form
          className="card grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            declarer.mutate();
          }}
        >
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Contrat concerné
            <select required value={contrat} onChange={(e) => setContrat(Number(e.target.value))} className="input w-full mt-0.5">
              <option value="">Choisir…</option>
              {contrats.map((c) => (
                <option key={c.id} value={c.id}>{c.assureur} — {c.numero}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Nature
            <select value={type} onChange={(e) => setType(e.target.value)} className="input w-full mt-0.5">
              <option value="dommage_materiel">Dommage matériel</option>
              <option value="dommage_corporel">Dommage corporel</option>
              <option value="vol_perte">Vol ou perte</option>
              <option value="rc_engagee">Responsabilité civile engagée</option>
              <option value="catastrophe_naturelle">Catastrophe naturelle</option>
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Survenu le
            <input required type="datetime-local" value={survenuLe} onChange={(e) => setSurvenuLe(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Lieu
            <input required value={lieu} onChange={(e) => setLieu(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Pendant l'événement
            <select value={evenement} onChange={(e) => setEvenement(e.target.value === '' ? '' : Number(e.target.value))} className="input w-full mt-0.5">
              <option value="">— aucun —</option>
              {evenements.map((ev) => (
                <option key={ev.id} value={ev.id}>{ev.nom}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Dégâts estimés (€)
            <input type="number" step="0.01" min="0" value={montant} onChange={(e) => setMontant(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400 sm:col-span-2 lg:col-span-3">
            Description — les faits, sans interprétation : c'est ce que l'assureur lira
            <textarea required rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <button type="submit" disabled={declarer.isPending} className="btn-primary flex items-center gap-2 w-fit">
            <Plus className="w-4 h-4" /> Déclarer le sinistre
          </button>
        </form>
      )}

      <div className="space-y-3">
        {sinistres.map((s) => (
          <div key={s.id} className="card space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-gray-900 dark:text-gray-100">{s.typeLibelle}</span>
              <span className="text-sm text-gray-500">· {dateFr(s.survenuLe)} · {s.lieu}</span>
              {s.evenementNom && <span className="text-sm text-gray-500">· pendant {s.evenementNom}</span>}
              <span className="ml-auto rounded-full px-2 py-0.5 text-xs bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
                {s.statutLibelle}
              </span>
            </div>
            <p className="text-sm text-gray-700 dark:text-gray-300">{s.description}</p>
            <div className="flex items-center gap-3 flex-wrap text-sm text-gray-500 dark:text-gray-400">
              <span>Contrat {s.contratNumero}</span>
              {s.montantEstimeCentimes !== null && <span>· estimé {euros(s.montantEstimeCentimes)}</span>}
              {s.transmisLe !== null ? (
                <span>· transmis le {dateFr(s.transmisLe)} ({s.joursAvantTransmission} j après les faits)</span>
              ) : (
                <span className="text-amber-700 dark:text-amber-400 font-medium">· PAS ENCORE TRANSMIS</span>
              )}
              {peutGerer && s.transmisLe === null && (
                <button
                  type="button"
                  onClick={() => transmettre.mutate(s.id)}
                  className="inline-flex items-center gap-1 rounded-lg border border-primary-500 px-2 py-1 text-xs text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20"
                >
                  <Send className="w-3.5 h-3.5" /> Transmettre à l'assureur
                </button>
              )}
              {peutGerer && (
                <select
                  value={s.statut}
                  onChange={(e) => changerStatut.mutate({ id: s.id, statut: e.target.value })}
                  aria-label={`Statut du sinistre ${s.id}`}
                  className="input text-xs py-1 ml-auto"
                >
                  <option value="declare">Déclaré</option>
                  <option value="transmis">Transmis</option>
                  <option value="en_instruction">En instruction</option>
                  <option value="clos">Clos</option>
                  <option value="rejete">Rejeté</option>
                </select>
              )}
            </div>
          </div>
        ))}
        {sinistres.length === 0 && (
          <p className="card text-sm text-gray-600 dark:text-gray-400">Aucun sinistre — c'est la bonne colonne du registre.</p>
        )}
      </div>
    </div>
  );
}

/* ---------------------------------- Biens ---------------------------------- */

function Biens({
  biens,
  contrats,
  peutGerer,
  onFait,
  onErreur,
}: {
  biens: { id: number; type: string; designation: string; contratNumero: string | null; valeurCentimes: number | null; immatriculation: string | null; couvert: boolean }[];
  contrats: AssuranceContrat[];
  peutGerer: boolean;
  onFait: () => void;
  onErreur: (e: Error) => void;
}) {
  const [type, setType] = useState('materiel');
  const [designation, setDesignation] = useState('');
  const [valeur, setValeur] = useState('');
  const [contrat, setContrat] = useState<number | ''>('');
  const [immatriculation, setImmatriculation] = useState('');

  const creer = useMutation({
    mutationFn: () =>
      creerAssuranceBien({
        type,
        designation,
        valeurCentimes: valeur === '' ? null : Math.round(Number(valeur) * 100),
        contrat: contrat === '' ? null : contrat,
        immatriculation: immatriculation || null,
      }),
    onSuccess: () => {
      setDesignation('');
      setValeur('');
      setImmatriculation('');
      onFait();
    },
    onError: onErreur,
  });

  const retirer = useMutation({
    mutationFn: (id: number) => supprimerAssuranceBien(id),
    onSuccess: onFait,
    onError: onErreur,
  });

  return (
    <div className="space-y-4">
      <p className="card text-sm text-gray-600 dark:text-gray-400">
        L'inventaire se tient <strong>avant</strong> le sinistre : c'est au moment du vol que
        l'assureur demande la liste et la valeur de ce qui était couvert — le pire moment pour la
        reconstituer de mémoire.
      </p>

      {peutGerer && (
        <form
          className="card grid gap-3 sm:grid-cols-2 lg:grid-cols-5"
          onSubmit={(e) => {
            e.preventDefault();
            creer.mutate();
          }}
        >
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Type
            <select value={type} onChange={(e) => setType(e.target.value)} className="input w-full mt-0.5">
              <option value="local">Local</option>
              <option value="materiel">Matériel</option>
              <option value="vehicule">Véhicule</option>
            </select>
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Désignation
            <input required value={designation} onChange={(e) => setDesignation(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Valeur (€)
            <input type="number" step="0.01" min="0" value={valeur} onChange={(e) => setValeur(e.target.value)} className="input w-full mt-0.5" />
          </label>
          <label className="block text-sm text-gray-600 dark:text-gray-400">
            Contrat
            <select value={contrat} onChange={(e) => setContrat(e.target.value === '' ? '' : Number(e.target.value))} className="input w-full mt-0.5">
              <option value="">— aucun —</option>
              {contrats.map((c) => (
                <option key={c.id} value={c.id}>{c.assureur} — {c.numero}</option>
              ))}
            </select>
          </label>
          {type === 'vehicule' && (
            <label className="block text-sm text-gray-600 dark:text-gray-400">
              Immatriculation
              <input value={immatriculation} onChange={(e) => setImmatriculation(e.target.value)} className="input w-full mt-0.5 font-mono" />
            </label>
          )}
          <button type="submit" disabled={creer.isPending} className="btn-primary flex items-center gap-2 w-fit">
            <Plus className="w-4 h-4" /> Ajouter à l'inventaire
          </button>
        </form>
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="py-1.5 pr-3">Type</th>
              <th className="py-1.5 pr-3">Désignation</th>
              <th className="py-1.5 pr-3">Valeur</th>
              <th className="py-1.5 pr-3">Contrat</th>
              <th className="py-1.5 pr-3">Couvert</th>
              {peutGerer && <th className="py-1.5">Retirer</th>}
            </tr>
          </thead>
          <tbody>
            {biens.map((b) => (
              <tr key={b.id} className="border-t border-gray-100 dark:border-gray-700">
                <td className="py-2 pr-3 capitalize">{b.type}</td>
                <td className="py-2 pr-3 font-medium text-gray-900 dark:text-gray-100">
                  {b.designation}
                  {b.immatriculation && <span className="ml-1 font-mono text-xs text-gray-500">{b.immatriculation}</span>}
                </td>
                <td className="py-2 pr-3 tabular-nums">{euros(b.valeurCentimes)}</td>
                <td className="py-2 pr-3">{b.contratNumero ?? '—'}</td>
                <td className="py-2 pr-3">
                  {b.couvert
                    ? <span className="text-emerald-700 dark:text-emerald-400">oui</span>
                    : <span className="font-medium text-amber-700 dark:text-amber-400">NON</span>}
                </td>
                {peutGerer && (
                  <td className="py-2">
                    <button
                      type="button"
                      onClick={() => retirer.mutate(b.id)}
                      aria-label={`Retirer ${b.designation}`}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                )}
              </tr>
            ))}
            {biens.length === 0 && (
              <tr><td colSpan={6} className="py-4 text-center text-gray-500">Inventaire vide.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
